import { FormDialog } from '@/components/FormDialog'
import { SelectedEquipmentCard } from '@/components/SelectedEquipmentCard'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useGetProducts } from '@/features/equipment/hooks/useProduct'
import { getProductName } from '@/features/equipment/utils/product'
import { isApiError } from '@/utils/error'
import { showError, showSuccess } from '@/utils/toast'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreateVendorProduct } from '../hooks/useVendors'
import type { Vendor } from '../types/vendor'

const emptyForm = {
  productId: '',
  spec: '',
  catalogUrl: '',
  price: '',
  warranty: '',
  maintenancePrice: '',
}
const MONEY_RE = /^-?(?:0|[1-9]\d*)(?:\.\d+)?$/

export function VendorProductDialog({
  vendor,
  open,
  onOpenChange,
}: {
  vendor: Vendor | null
  open: boolean
  onOpenChange: (o: boolean) => void
}) {
  const { t } = useTranslation()
  const [form, setForm] = useState(emptyForm)
  const { data: products = [], isLoading: isLoadingProducts } = useGetProducts(undefined, open)
  const createVendorProduct = useCreateVendorProduct()
  const selectedProduct = products.find(product => product.id === form.productId)
  const productOptions = products.map(product => ({
    value: product.id,
    label: getProductName(product.name, product.brand, product.model),
    description: product.categoryName,
    searchValue: `${product.name || ''} ${product.brand || ''} ${product.model || ''} ${product.categoryName || ''}`,
  }))
  const isInvalid =
    !form.productId ||
    !form.spec ||
    !form.catalogUrl ||
    !form.warranty ||
    !MONEY_RE.test(form.price) ||
    !MONEY_RE.test(form.maintenancePrice)

  useEffect(() => {
    if (!open) setForm(emptyForm)
  }, [open])

  async function handleSubmit() {
    if (isInvalid || !vendor) return

    try {
      await createVendorProduct.mutateAsync({ vendorId: vendor.id, payload: form })

      showSuccess(t('vendors.productCreateSuccess'))
      onOpenChange(false)
    } catch (error) {
      if (isApiError(error)) showError(error.message)
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('vendors.addProduct')}
      onSubmit={handleSubmit}
      isLoading={createVendorProduct.isPending}
      isInvalid={isInvalid}
      contentTestId="vendor-product-dialog"
      submitTestId="vendor-product-form-submit"
    >
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs">{t('vendors.product')} *</Label>
        <SearchableSelect
          value={form.productId}
          onValueChange={productId => setForm({ ...form, productId })}
          options={productOptions}
          placeholder={t('vendors.selectProduct')}
          searchPlaceholder={t('vendors.searchProduct')}
          emptyText={isLoadingProducts ? t('common.loading') : t('vendors.noProducts')}
          disabled={isLoadingProducts}
          triggerClassName="w-full bg-secondary/50 text-xs"
          testId="vendor-product-form-product"
        />
      </div>
      {selectedProduct && (
        <SelectedEquipmentCard
          name={selectedProduct.name}
          brand={selectedProduct.brand}
          model={selectedProduct.model}
          categoryName={selectedProduct.categoryName}
        />
      )}
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('vendors.price')} *</Label>
          <Input
            data-testid="vendor-product-form-price"
            inputMode="decimal"
            value={form.price}
            onChange={e => setForm({ ...form, price: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder="0.00"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('vendors.warranty')} *</Label>
          <Input
            data-testid="vendor-product-form-warranty"
            value={form.warranty}
            onChange={e => setForm({ ...form, warranty: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
          />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs">
          {t('vendors.maintenance')} ({t('vendors.bahtPerYear')}) *
        </Label>
        <Input
          data-testid="vendor-product-form-maintenance-price"
          inputMode="decimal"
          value={form.maintenancePrice}
          onChange={e => setForm({ ...form, maintenancePrice: e.target.value })}
          className="h-9 bg-secondary/50 text-xs"
          placeholder="0.00"
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs">{t('vendors.spec')} *</Label>
        <Input
          data-testid="vendor-product-form-spec"
          value={form.spec}
          onChange={e => setForm({ ...form, spec: e.target.value })}
          className="h-9 bg-secondary/50 text-xs"
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs">{t('vendors.catalogUrl')} *</Label>
        <Input
          data-testid="vendor-product-form-catalog-url"
          value={form.catalogUrl}
          onChange={e => setForm({ ...form, catalogUrl: e.target.value })}
          className="h-9 bg-secondary/50 text-xs"
          placeholder="https://"
        />
      </div>
    </FormDialog>
  )
}
